import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageCircle, Paperclip, Image as ImageIcon, X, Reply, Edit2, Check, X as XIcon, AlertTriangle, MoreVertical, Flag } from 'lucide-react';
import { sendBatchChatMessage, subscribeToBatchChat, deleteBatchChatMessage, updateBatchChatMessage } from '../../firebase/collections';
import { uploadFile } from '../../firebase/r2storage';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const BatchChat = ({ batchId }) => {
  const { currentUser, userProfile } = useRealtime();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null); // Message being replied to
  const [editingMessage, setEditingMessage] = useState(null); // Message being edited
  const [openMenuId, setOpenMenuId] = useState(null); // Track which message menu is open
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!batchId) return;

    const unsubscribe = subscribeToBatchChat(batchId, (result) => {
      setLoading(false);
      if (result.success) {
        // Filter out any messages without required fields and ensure they have IDs
        const validMessages = (result.data || []).filter(msg => 
          msg && msg.id && (msg.message || msg.fileUrl)
        );
        console.log('Loaded messages:', validMessages.length);
        setMessages(validMessages);
        // Scroll to bottom when new messages arrive
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        // Show error to user
        console.error('Error loading messages:', result.error);
        toast.error(result.error || 'Failed to load messages');
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [batchId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || sending) return;

    // If editing, update the message instead
    if (editingMessage) {
      try {
        setSending(true);
        const result = await updateBatchChatMessage(editingMessage.id, {
          message: newMessage.trim()
        });

        if (result.success) {
          setNewMessage('');
          setEditingMessage(null);
          toast.success('Message updated');
        } else {
          console.error('Failed to update message:', result.error);
          toast.error('Failed to update message: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        toast.error('Error updating message: ' + error.message);
      } finally {
        setSending(false);
      }
      return;
    }

    // Otherwise, send new message
    try {
      setSending(true);
      const result = await sendBatchChatMessage({
        batchId,
        senderId: currentUser.uid,
        senderName: userProfile?.displayName || currentUser.displayName || 'Anonymous',
        senderEmail: currentUser.email,
        message: newMessage.trim(),
        messageType: 'text',
        replyTo: replyingTo?.id || null,
        replyToMessage: replyingTo?.message || null,
        replyToSender: replyingTo?.senderName || null
      });

      if (result.success) {
        setNewMessage('');
        setReplyingTo(null);
        console.log('Message sent successfully:', result.id);
      } else {
        console.error('Failed to send message:', result.error);
        toast.error('Failed to send message: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error sending message: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !currentUser) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      const fileName = `batch-chat/${batchId}/${Date.now()}_${file.name}`;
      const uploadResult = await uploadFile(file, fileName);

      if (uploadResult.success) {
        const result = await sendBatchChatMessage({
          batchId,
          senderId: currentUser.uid,
          senderName: userProfile?.displayName || currentUser.displayName || 'Anonymous',
          senderEmail: currentUser.email,
          message: '',
          messageType: 'image',
          fileUrl: uploadResult.url,
          fileName: file.name
        });

        if (!result.success) {
          toast.error('Failed to send image');
        }
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      toast.error('Error uploading file: ' + error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      const result = await deleteBatchChatMessage(messageId);
      if (result.success) {
        toast.success('Message deleted');
      } else {
        toast.error('Failed to delete message');
      }
    } catch (error) {
      toast.error('Error deleting message: ' + error.message);
    }
  };

  const handleReplyToMessage = (message) => {
    setReplyingTo({
      id: message.id,
      senderName: message.senderName,
      message: message.message || (message.messageType === 'image' ? 'Image' : 'Message')
    });
    setEditingMessage(null);
    // Focus on input
    setTimeout(() => {
      document.querySelector('input[type="text"]')?.focus();
    }, 100);
  };

  const handleEditMessage = (message) => {
    // Only allow editing text messages
    if (message.messageType !== 'text') {
      toast.error('Only text messages can be edited');
      return;
    }
    setEditingMessage(message);
    setNewMessage(message.message || '');
    setReplyingTo(null);
    // Focus on input
    setTimeout(() => {
      document.querySelector('input[type="text"]')?.focus();
    }, 100);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setEditingMessage(null);
    setNewMessage('');
  };

  const handleReportMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to report this message?')) {
      return;
    }
    // TODO: Implement report message functionality
    toast.success('Message reported');
    setOpenMenuId(null);
  };

  const toggleMenu = (messageId) => {
    setOpenMenuId(openMenuId === messageId ? null : messageId);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.message-menu')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const findMessageById = (messageId) => {
    return messages.find(msg => msg.id === messageId);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const isOwnMessage = (message) => {
    return message.senderId === currentUser?.uid;
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg shadow-md">
      {/* Header */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Batch Chat</h2>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-2 sm:space-y-3 min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading messages...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No messages yet. Start the conversation!</p>
            </div>
          </div>
        ) : (
          messages.map((message) => {
            const repliedToMessage = message.replyTo ? findMessageById(message.replyTo) : null;
            return (
              <div
                key={message.id}
                className={`flex group ${isOwnMessage(message) ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] sm:max-w-xs lg:max-w-md px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg relative overflow-visible ${
                    isOwnMessage(message)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                  }`}
                >
                  {/* 3-dots menu in top right */}
                  <div className="absolute top-0 -right-2 message-menu z-20">
                    <button
                      onClick={() => toggleMenu(message.id)}
                      className={`p-0.5 touch-manipulation rounded-tr-lg transition-all ${
                        isOwnMessage(message)
                          ? 'opacity-90 hover:opacity-100 hover:bg-white/20 text-white'
                          : 'opacity-70 hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}
                      title="More options"
                    >
                      <MoreVertical className="w-3.5 h-3.5" />
                    </button>
                    {openMenuId === message.id && (
                      <div className="absolute right-0 top-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50 min-w-[140px]">
                        <button
                          onClick={() => {
                            handleReplyToMessage(message);
                            setOpenMenuId(null);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Reply className="w-4 h-4" />
                          Reply
                        </button>
                        {isOwnMessage(message) && (
                          <>
                            <button
                              onClick={() => {
                                handleEditMessage(message);
                                setOpenMenuId(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                handleDeleteMessage(message.id);
                                setOpenMenuId(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <X className="w-4 h-4" />
                              Delete
                            </button>
                          </>
                        )}
                        {!isOwnMessage(message) && (
                          <button
                            onClick={() => {
                              handleReportMessage(message.id);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <Flag className="w-4 h-4" />
                            Report
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Reply context */}
                  {message.replyTo && (repliedToMessage || message.replyToMessage) && (
                    <div className={`mb-1 pb-1 border-l-2 pl-1.5 pr-8 ${
                      isOwnMessage(message) 
                        ? 'border-blue-300 opacity-80' 
                        : 'border-gray-400 dark:border-gray-500'
                    }`}>
                      <div className="text-[10px] sm:text-xs font-medium opacity-75 leading-tight">
                        {message.replyToSender || repliedToMessage?.senderName || 'Unknown'}
                      </div>
                      <div className={`text-[10px] sm:text-xs truncate leading-tight ${
                        isOwnMessage(message) ? 'opacity-70' : 'opacity-60'
                      }`}>
                        {repliedToMessage?.message || message.replyToMessage || 'Message deleted'}
                      </div>
                    </div>
                  )}

                  <div className={`text-[10px] sm:text-xs font-semibold mb-0.5 pr-8 ${
                    isOwnMessage(message) 
                      ? 'text-blue-100' 
                      : 'text-gray-600 dark:text-gray-300'
                  }`}>
                    {message.senderName || 'Anonymous'}
                  </div>
                  {message.messageType === 'image' && message.fileUrl ? (
                    <div className="pr-8">
                      <img
                        src={message.fileUrl}
                        alt={message.fileName || 'Image'}
                        className="max-w-full h-auto rounded mb-0.5 cursor-pointer"
                        onClick={() => window.open(message.fileUrl, '_blank')}
                      />
                      {message.message && (
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm">{message.message}</p>
                      )}
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed pr-8">{message.message}</p>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Warning Banner */}
      <div className="px-3 sm:px-4 py-2 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800 flex-shrink-0">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-yellow-800 dark:text-yellow-300 leading-relaxed">
            <span className="font-semibold">Warning:</span> If you spam or use abusive language, your chat may be reported to your teacher and admin. This may result in the cancellation of your enrollment. Please maintain proper behavior.
          </p>
        </div>
      </div>

      {/* Input */}
      <div className="px-3 sm:px-4 py-3 sm:py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        {/* Reply Preview */}
        {replyingTo && (
          <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                  Replying to {replyingTo.senderName}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                  {replyingTo.message && replyingTo.message.length > 100 
                    ? replyingTo.message.substring(0, 100) + '...' 
                    : replyingTo.message || 'Image'}
                </div>
              </div>
              <button
                onClick={handleCancelReply}
                className="flex-shrink-0 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1"
                title="Cancel reply"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Edit Mode Indicator */}
        {editingMessage && (
          <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit2 className="w-4 h-4 text-yellow-700 dark:text-yellow-300" />
                <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">
                  Editing message
                </span>
              </div>
              <button
                onClick={handleCancelReply}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              if (e.target.files[0]) {
                handleFileUpload(e.target.files[0]);
              }
            }}
          />
          {!editingMessage && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 flex-shrink-0"
              title="Upload image"
            >
              {uploading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              ) : (
                <ImageIcon className="w-5 h-5" />
              )}
            </button>
          )}
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={editingMessage ? "Edit message..." : replyingTo ? `Reply...` : "Type a message..."}
            className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending || uploading}
            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 transition-colors"
            title={editingMessage ? "Save changes" : "Send message"}
          >
            {sending ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : editingMessage ? (
              <Check className="w-5 h-5" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BatchChat;

